/**
 * One Million Lights — the campaign, rebuilt inside Soulumo.
 *
 * Mar 3 2026 · Rebuilt from Michael's updated brief:
 *   • Cinematic black-and-gold light experience
 *   • Very little writing in the opening screen
 *   • One clear £1 invitation (preview state — payments not open)
 *   • A simple choice of intention
 *   • A beautiful illumination and Founding Light reveal
 *   • A personalised share card with name, intention, unique number
 *   • Wider Reclaim story / project PDFs / doorways moved below the
 *     fold behind a single "Explore the vision" reveal
 *
 * Honest state (per the brief):
 *   • Payments are NOT connected. Every reservation flags
 *     `preview: True` and the UI carries a "Preview · no gift taken"
 *     chip everywhere the £1 CTA appears.
 *   • Foundations memberships are NOT auto-issued from this page.
 *     The admin-grant endpoint (built earlier) handles that once a
 *     recipient distribution partner is confirmed.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { GuideLiteEntry, GuideBubble } from "../Guide/GuideLitePage";
import { ByReclaim } from "../../components/ecosystem/ReclaimNav";

import axios from "axios";
import { useNavigate } from "react-router-dom";
import SoulumoWord from "../../components/SoulumoWord";
import InstallOrb from "../../components/InstallOrb";
import LivingLightsField from "./LivingLightsField";
import LivingGlobe from "./LivingGlobe";
import * as openingAudio from "./openingAudio";
import FoundationsRevealSection from "./FoundationsRevealSection";
import FoundingLightModal from "./FoundingLightModal";
import GlobalAlignmentCard from "./GlobalAlignmentCard";
import PostDonationModal from "./PostDonationModal";
import ImpactReceipt from "./ImpactReceipt";
import CirclesOfCareSheet from "./CirclesOfCareSheet";
import { MOTIF_BY_ID } from "./DoorwayMotifs";
import LightsPlacementTicker from "./LightsPlacementTicker";
import {
  LiveImpactBand,
  RippleEffectSection,
  BehindEveryPersonSection,
  CircleOfCarePreview,
  UnifyingEssenceSection,
  PartnershipSection,
  TransparencySection,
  FoundingLightThankYouSection,
  ClosingMillionCarersSection,
  CARE_KEYFRAMES,
} from "./CareNarrativeSections";

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem("reclaim_token");
const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const TOK = {
  ink: "#050a17",
  bg: "radial-gradient(ellipse at 50% 20%, #131f38 0%, #0a132a 40%, #030710 85%)",
  cream: "#f5ecd9",
  // Feb 2026 · Michael — legibility pass. Text against the deep-night
  // background was fading out at low opacities; boosted every tier
  // one notch for readability without losing the dreamlike palette.
  creamReadable: "rgba(245,236,217,0.98)",
  creamDim: "rgba(245,236,217,0.86)",
  creamMist: "rgba(245,236,217,0.66)",
  gold: "#e0a97a",
  goldSoft: "rgba(224,169,122,0.98)",
  goldMist: "rgba(224,169,122,0.34)",
};

const BLESSED_FALLBACK = [
  "Love", "Hope", "Courage", "Peace",
  "Belief", "Kindness", "Freedom", "Connection",
];

// Multi-currency ladders (Feb 2026 v2 review · Michael). Strict 1:1
// — each unit of currency opens Foundations for one person. The
// package_id we send to Stripe is derived as
// `oml_founding_light_<currency_lower>_<amount>` and mirrors the
// backend PACKAGES dict in /app/backend/routes/oml_payments.py.
const CURRENCY_LADDERS = {
  GBP: [1, 5, 10, 20],
  USD: [1, 5, 10, 25],
  EUR: [1, 5, 10, 20],
};
const CURRENCY_SYMBOL = { GBP: "£", USD: "$", EUR: "€" };

/* ─────────────────────────────────────────────────────────────────
 * Root — a mostly-black canvas with the flow above the fold and
 * the "Explore the vision" reveal below.
 * ────────────────────────────────────────────────────────────── */
export default function OneMillionLightsPage() {
  const nav = useNavigate();
  const [step, setStep] = useState("arrival"); // arrival · intention · gift · illumination · share
  const [intentions, setIntentions] = useState(BLESSED_FALLBACK);
  const [intention, setIntention] = useState(null);
  const [ownWord, setOwnWord] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [light, setLight] = useState(null);
  const [count, setCount] = useState(null);
  const [reserving, setReserving] = useState(false);
  const [error, setError] = useState(null);
  // Which amount tier the contributor picks. £1 is the default and
  // remains the emotional anchor — the pills scale the gift, they
  // do not raise the price of a single Founding Light.
  // Feb 2026 · Michael. Pre-select the middle tier so a hurried
  // visitor can tap `Place my light · £5` without choosing an amount.
  // £1 is still the headline; anyone who wants the entry gift taps
  // down to it before continuing.
  const [amountGbp, setAmountGbp] = useState(5);
  // Currency the visitor is transacting in. Feb 2026 v2 review ·
  // Michael. Starts from CF-IPCountry via /api/oml/geo, then the
  // visitor can override with the £ · Change switcher. Persisted in
  // localStorage so a returning visitor keeps their choice.
  const [currency, setCurrencyState] = useState(() => {
    try {
      const saved = localStorage.getItem("oml_currency");
      if (saved && ["GBP", "USD", "EUR"].includes(saved)) return saved;
    } catch (_) {}
    return "GBP";
  });
  const setCurrency = (cur) => {
    setCurrencyState(cur);
    try { localStorage.setItem("oml_currency", cur); } catch (_) {}
    // Snap the amount back to the middle tier of the new ladder so
    // we never leave a stale £5 selected on the USD ladder.
    setAmountGbp(CURRENCY_LADDERS[cur]?.[1] ?? 5);
  };
  // Recent lights for the arrival-screen social-proof ticker + the
  // Living Field label bubbles. Refreshed every 45s while on page.
  const [recent, setRecent] = useState([]);
  // Free Founding Light modal — the LEGALLY-SEPARATED free path to
  // the 40% first-year offer. Not conditional on any payment.
  const [showFL, setShowFL] = useState(false);
  const [flJoined, setFlJoined] = useState(() => {
    try {
      const raw = localStorage.getItem("oml_founding_light");
      return raw ? JSON.parse(raw) : null;
    } catch (_) { return null; }
  });
  // Post-donation modal — appears once after a paid checkout lands
  // back on the illumination screen. Dismissal is remembered per
  // light-id in localStorage so it never re-fires on a refresh.
  const [showPostDonation, setShowPostDonation] = useState(false);
  // Feb 10 2026 · Simplified landing: the below-the-fold content
  // (Foundations reveal + Explore vision + FAQs) is now hidden by
  // default and only revealed when the visitor taps one of the
  // three secondary chips (Gift · Explore Apps · FAQs). Keeps the
  // arrival screen uncluttered so the two primary doorways lead.
  const [openBelow, setOpenBelow] = useState(null); // 'explore' | 'faqs' | 'gift' | 'founding' | null
  const exploreAnchorRef = useRef(null);
  const faqsAnchorRef = useRef(null);
  const giftAnchorRef = useRef(null);
  const foundingAnchorRef = useRef(null);
  // Phase 2 · The "Your Light" moment. When Stripe redirects back
  // to /?brand=oml&paid=<sid>, we resolve the just-placed light's
  // country + jittered display coord and pass it to LivingGlobe
  // so the white pulse plays over the globe (rather than jumping
  // straight into the old illumination screen). After the 11s
  // white→gold transition we surface the share moment.
  const [freshLight, setFreshLight] = useState(null);
  // Feb 22 2026 · Impact Receipt — a share-friendly card that
  // pops after Stripe confirms the payment. Holds real data from
  // /api/oml/impact/{sid}: donor's N lights + running total + today's
  // world count + today's top countries. Cleared when the donor
  // dismisses the receipt.
  const [impactReceipt, setImpactReceipt] = useState(null);
  const [freshFaded, setFreshFaded] = useState(false);
  // ── Circles of Care · Feb 23 2026 ──────────────────────────
  // Sheet gets a `pending` payload the moment the donor hits
  // "Place light". If they cancel, we roll back the reservation
  // flow. On confirm we merge the circles payload into the
  // POST /api/oml/checkout body and redirect to Stripe.
  const [showCirclesSheet, setShowCirclesSheet] = useState(false);
  // Feb 11 2026 · Referrer capture. If a visitor lands with
  // ?ref=CODE, persist it in localStorage for 30 days so a friend
  // can wander the site before deciding to contribute. Cleaned URL
  // afterwards so the code doesn't stick to bookmarks or shares.
  useEffect(() => {
    try {
      const q = new URLSearchParams(window.location.search).get("ref");
      if (q && q.trim()) {
        const code = q.trim().toUpperCase().slice(0, 32);
        localStorage.setItem("oml_ref_code", code);
        localStorage.setItem("oml_ref_code_at", String(Date.now()));
        // Strip the ref param from the URL so the emotional canvas
        // stays clean.
        const clean = new URL(window.location.href);
        clean.searchParams.delete("ref");
        window.history.replaceState({}, "", clean.pathname + (clean.search || "") + clean.hash);
      } else {
        // Expire the cookie after 30 days.
        const at = Number(localStorage.getItem("oml_ref_code_at") || 0);
        if (at && Date.now() - at > 30 * 24 * 60 * 60 * 1000) {
          localStorage.removeItem("oml_ref_code");
          localStorage.removeItem("oml_ref_code_at");
        }
      }
    } catch (_) { /* private-mode / SSR — noop */ }
  }, []);

  const scrollToRef = (ref) => {
    setTimeout(() => {
      try { ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }); } catch {}
    }, 80);
  };
  const handleExplore = () => { setOpenBelow("explore"); scrollToRef(exploreAnchorRef); };
  const handleFaqs = () => { setOpenBelow("faqs"); scrollToRef(faqsAnchorRef); };
  // Feb 11 2026 · Michael's ask: the Gift chip should NOT open the
  // native share sheet — it should explain what the actual gift IS
  // (a gifted Soulumo Foundations membership). Native share was
  // confusing recipients who thought they were being asked to send
  // a link, not learn about the offering.
  const handleGift = () => { setOpenBelow("gift"); scrollToRef(giftAnchorRef); };
  const handleFounding = () => { setOpenBelow("founding"); scrollToRef(foundingAnchorRef); };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/api/one-million-lights/recent?limit=20`);
        if (!cancelled && r.data?.recent) setRecent(r.data.recent);
      } catch (_) { /* silent */ }
    };
    load();
    const iv = setInterval(load, 45000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  // Feb 2026 v2 review · Michael. Auto-suggest a currency based on
  // Cloudflare's CF-IPCountry header at first arrival. Only kicks
  // in if the visitor has not chosen manually before — we never
  // override a saved choice.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const saved = localStorage.getItem("oml_currency");
        if (saved && ["GBP", "USD", "EUR"].includes(saved)) return;
      } catch (_) {}
      try {
        const r = await axios.get(`${API}/api/oml/geo`);
        const cur = r?.data?.currency;
        if (!cancelled && cur && ["GBP", "USD", "EUR"].includes(cur) && cur !== "GBP") {
          setCurrency(cur);
        }
      } catch (_) {}
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fade the ambient bed out fully when the OML flow unmounts so
  // audio doesn't leak into other brand routes.
  useEffect(() => () => { try { openingAudio.stop(); } catch (_) {} }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [ints, mine, c] = await Promise.all([
          axios.get(`${API}/api/one-million-lights/intentions`).catch(() => null),
          getToken() ? axios.get(`${API}/api/one-million-lights/mine`, auth()).catch(() => null) : null,
          axios.get(`${API}/api/one-million-lights/count`).catch(() => null),
        ]);
        if (cancelled) return;
        if (ints?.data?.intentions) setIntentions(ints.data.intentions);
        if (c?.data) setCount(c.data);
        // Feb 11 2026 · Michael: the arrival must ALWAYS lead with
        // the Living Globe — returning users included. We still
        // remember the placed light so the "See my light" chip
        // (top-left of arrival) can deep-link back into the
        // illumination screen when they choose to look again, but
        // we no longer auto-jump past the globe on load.
        if (mine?.data && mine.data.id) {
          setLight(mine.data);
          setIntention(mine.data.intention);
          setDisplayName(mine.data.display_name || "");
        }
      } catch (_) {/* silent */}
    })();
    return () => { cancelled = true; };
  }, []);

  const reduceMotion = useMemo(
    () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    []
  );

  const finalIntention = (intention === "__own__" ? ownWord : intention || "").trim();

  // ── Circles of Care · checkout kickoff ─────────────────────
  // The donor's tap on "Place my light" no longer goes straight
  // to Stripe. It opens the Circles sheet where they choose how
  // the gift travels: keep it · name people · pass it on for us
  // to allocate. Sheet returns a `circlesPayload` that gets
  // merged into POST /api/oml/checkout.
  const placeLight = () => {
    if (!finalIntention) return;
    setError(null);
    setShowCirclesSheet(true);
  };

  const runCheckout = async (circlesPayload) => {
    setShowCirclesSheet(false);
    setReserving(true);
    setError(null);
    try {
      // Stable per-browser visitor id. Persisted so a returning
      // visitor keeps the same light number across reloads.
      let vid = null;
      try {
        vid = localStorage.getItem("oml_visitor_id");
        if (!vid) {
          vid = (typeof crypto !== "undefined" && crypto.randomUUID)
            ? crypto.randomUUID()
            : String(Date.now()) + Math.random().toString(36).slice(2);
          localStorage.setItem("oml_visitor_id", vid);
        }
      } catch (_) { /* private mode — visitor stays session-scoped */ }
      // Pull the founding-light email (if the visitor signed up) so
      // the light record links back to their Founding Light record.
      let email = null;
      try {
        const fl = JSON.parse(localStorage.getItem("oml_founding_light") || "null");
        email = fl?.email || null;
      } catch (_) { /* fine */ }

      // 1 · Reserve the preview light (PUBLIC — no auth required).
      const r = await axios.post(
        `${API}/api/one-million-lights/reserve`,
        { intention: finalIntention, name: displayName || null, visitor_id: vid, email },
      );
      setLight(r.data);

      // 2 · Kick off the Stripe checkout for the chosen amount and
      //     redirect. On completion Stripe returns to /?brand=oml&paid=...
      const co = await axios.post(
        `${API}/api/oml/checkout`,
        {
          package_id: `oml_founding_light_${currency.toLowerCase()}_${amountGbp}`,
          origin_url: window.location.origin,
          light_id: r.data.id,
          // Feb 11 2026 · Referrer attribution. Read once from URL
          // (?ref=CODE) then persisted in localStorage for the 30-day
          // cookie window so a friend can browse before contributing.
          referrer_code: (function () {
            try { return localStorage.getItem("oml_ref_code") || null; } catch { return null; }
          })(),
          // Feb 23 2026 · Circles of Care payload.
          ...(circlesPayload || {}),
        },
        auth(),
      );
      if (co.data?.checkout_url) {
        window.location.href = co.data.checkout_url;
        return;
      }
      // Fallback (payments off) — jump straight to illumination in preview.
      setStep("illumination");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(detail === "intention_empty" ? "Please choose your word first." : "Could not place your light. Please try again.");
    } finally {
      setReserving(false);
    }
  };

  // After a successful Stripe checkout, Stripe bounces us back to
  // /?brand=oml&paid=<session_id>. Poll the OML payment-status
  // endpoint until it flips to `paid`, then reload the light doc
  // so the illumination screen renders WITHOUT the preview chip.
  //
  // QA shortcut — /?brand=oml&debug_postdonation=1 opens the modal
  // over the current state so the copy/design can be reviewed
  // without a real Stripe round-trip. Preview-only convenience.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("debug_postdonation") === "1") {
      setShowPostDonation(true);
    }
    // Feb 22 2026 · Preview the Impact Receipt overlay without a
    // real Stripe round-trip. Only intended for QA / design review.
    if (params.get("debug_receipt") === "1") {
      setImpactReceipt({
        session_id: "cs_test_preview",
        light_id: "preview_light",
        lights: 5,
        amount: 5.0,
        currency: "gbp",
        country: "GB",
        placed_at: new Date().toISOString(),
        running_total: 18410,
        target: 1_000_000,
        today_count: 42,
        today_countries: [
          { cc: "GB", n: 18 },
          { cc: "US", n: 12 },
          { cc: "DE", n: 6 },
          { cc: "IE", n: 3 },
          { cc: "AU", n: 2 },
          { cc: "IN", n: 1 },
        ],
      });
    }
    const sid = params.get("paid");
    if (!sid) return;
    let cancelled = false;
    (async () => {
      for (let i = 0; i < 20 && !cancelled; i++) {
        try {
          const s = await axios.get(`${API}/api/oml/payment-status/${sid}`);
          if (s.data?.payment_status === "paid") {
            // Fetch the Impact Receipt in parallel with the light
            // lookups so the share-friendly card is ready by the
            // time the globe pulse fades. Feb 22 2026 · Michael.
            try {
              const impact = await axios.get(`${API}/api/oml/impact/${sid}`);
              if (impact.data && !cancelled) setImpactReceipt(impact.data);
            } catch (_) {}
            // /mine requires auth — most OML donors are anonymous.
            // Wrap so a 401 does NOT trigger any global redirect.
            let mine = { data: null };
            try {
              const tok = localStorage.getItem("reclaim_token");
              if (tok) mine = await axios.get(`${API}/api/one-million-lights/mine`, { headers: { Authorization: `Bearer ${tok}` } });
            } catch (_) {}
            if (mine.data?.id) setLight(mine.data);
            // Phase 2 · resolve the light's country + jittered coord
            // so the LivingGlobe can play the white pulse over it.
            // If the light-by-session lookup fails (network blip,
            // record still committing), fall through to the classic
            // illumination screen so we never trap the user.
            let usedGlobe = false;
            try {
              const fl = await axios.get(`${API}/api/one-million-lights/light-by-session/${sid}`);
              if (fl.data?.lat != null && fl.data?.lng != null) {
                setFreshLight({
                  lat: fl.data.lat,
                  lng: fl.data.lng,
                  count: fl.data.count || 1,
                  country_name: fl.data.country_name,
                  display_name: fl.data.display_name,
                  intention: fl.data.intention,
                });
                setStep("arrival");
                usedGlobe = true;
              }
            } catch (_) {}
            if (!usedGlobe) {
              setStep("illumination");
            }
            // Post-donation invitation — only if this light has not
            // already dismissed it (survives a refresh).
            const lightId = mine.data?.id;
            let alreadyDismissed = false;
            try {
              alreadyDismissed = !!(lightId && localStorage.getItem(`oml_postdonation_dismissed_${lightId}`));
            } catch (_) {}
            if (!alreadyDismissed && !usedGlobe) {
              // On the classic illumination path only — the globe
              // has its own share moment after the fade-to-gold.
              setTimeout(() => setShowPostDonation(true), 1800);
            }
            // Clean the query string.
            window.history.replaceState({}, "", "/?brand=oml");
            return;
          }
        } catch (_) {}
        await new Promise((res) => setTimeout(res, 1500));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen relative overflow-x-hidden" style={{ background: TOK.bg, color: TOK.cream }} data-testid="oml-page">
      {step === "arrival" && <GuideBubble to="/lights/guide" label="One Million Lights" introKey="lights_guide_intro_seen" />}
      <BackgroundStars reduceMotion={reduceMotion} />

      {/* Top-bar: context-aware back link + wordmark, nothing else.
          On the arrival step there is nowhere to go back to, so the
          button is hidden. On any wizard step it walks the user
          back through the flow rather than leaving the site. */}
      <div className="relative z-10 max-w-2xl mx-auto px-5 pt-6 flex items-center justify-between">
        {step === "arrival" ? (
          <ByReclaim product="lights" />
        ) : (
          <button
            onClick={() => {
              const order = ["arrival", "intention", "gift", "illumination", "share"];
              const idx = order.indexOf(step);
              const prev = idx > 0 ? order[idx - 1] : "arrival";
              setStep(prev);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            data-testid="oml-back"
            className="text-[0.62rem] uppercase tracking-[0.28em]"
            style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
          >
            ← Back
          </button>
        )}
        <SoulumoWord size="xs" />
      </div>

      {/* ── The flow (above the fold) ────────────────────── */}
      <div className="relative z-10 max-w-2xl mx-auto px-5 pt-10 pb-16 flex flex-col items-center text-center">
        {step === "arrival" && (
          <ArrivalScreen
            onEnter={() => setStep("intention")}
            onJoinFree={() => setShowFL(true)}
            onGift={handleGift}
            onExplore={handleExplore}
            onFaqs={handleFaqs}
            onFounding={handleFounding}
            flJoined={!!flJoined}
            reduceMotion={reduceMotion}
            count={count}
            recent={recent}
            freshLight={freshLight}
            freshFaded={freshFaded}
            onFreshFaded={() => setFreshFaded(true)}
            myPlacedLight={light}
            onSeeMyLight={() => setStep("illumination")}
          />
        )}
        {step === "intention" && (
          <IntentionScreen
            intentions={intentions}
            intention={intention}
            setIntention={setIntention}
            ownWord={ownWord}
            setOwnWord={setOwnWord}
            onContinue={() => setStep("gift")}
            onBack={() => setStep("arrival")}
            reduceMotion={reduceMotion}
          />
        )}
        {step === "gift" && (
          <GiftScreen
            finalIntention={finalIntention}
            displayName={displayName}
            setDisplayName={setDisplayName}
            reserving={reserving}
            error={error}
            onPlace={placeLight}
            onBack={() => setStep("intention")}
            count={count}
            amountGbp={amountGbp}
            setAmountGbp={setAmountGbp}
            currency={currency}
            setCurrency={setCurrency}
            reduceMotion={reduceMotion}
          />
        )}
        {step === "illumination" && light && (
          <IlluminationScreen
            light={light}
            onShare={() => setStep("share")}
            onNew={() => { setStep("intention"); }}
            reduceMotion={reduceMotion}
          />
        )}
        {step === "share" && light && (
          <ShareScreen light={light} onBack={() => setStep("illumination")} />
        )}
      </div>

      {/* ── Below the fold — CHIP-GATED reveals only ─
          Feb 13 2026 · Michael. The arrival hero stays uncluttered
          — nothing appears below the hero unless the visitor taps
          one of the four chips (Gift · Apps · Founding · FAQs).
          Each chip reveals a focused block themed to that intent. */}
      <div className="relative z-10 max-w-4xl mx-auto px-5 pb-16">
        {step === "arrival" && openBelow === "gift" && (
          <div ref={giftAnchorRef} data-testid="oml-gift-anchor">
            <GiftRevealBlock
              onPlaceLight={() => { setOpenBelow(null); setStep("intention"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              onClose={() => setOpenBelow(null)}
            />
          </div>
        )}

        {step === "arrival" && openBelow === "founding" && (
          <div ref={foundingAnchorRef} data-testid="oml-founding-anchor">
            <FoundingRevealBlock
              onJoinFree={() => setShowFL(true)}
              flJoined={!!flJoined}
              onClose={() => setOpenBelow(null)}
              count={count}
            />
          </div>
        )}

        {step === "arrival" && openBelow === "gift" && (
          <div ref={giftAnchorRef} data-testid="oml-gift-anchor">
            <GiftRevealBlock
              onPlaceLight={() => { setOpenBelow(null); setStep("intention"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              onClose={() => setOpenBelow(null)}
            />
          </div>
        )}

        {step === "arrival" && openBelow === "founding" && (
          <div ref={foundingAnchorRef} data-testid="oml-founding-anchor">
            <FoundingRevealBlock
              onJoinFree={() => setShowFL(true)}
              flJoined={!!flJoined}
              onClose={() => setOpenBelow(null)}
              count={count}
            />
          </div>
        )}

        {step === "arrival" && openBelow === "explore" && (
          <div ref={exploreAnchorRef} data-testid="oml-explore-anchor" className="mt-6">
            {/* Feb 13 2026 · Michael. Apps reveal now shows ONLY the
                wider ecosystem (the six creations). Soulumo
                Foundations already lives inside the Gift reveal, so
                we do not repeat it here. */}
            <div className="flex items-baseline justify-between gap-3 mb-2">
              <span
                className="uppercase"
                style={{
                  color: "rgba(224,169,122,0.92)",
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.6rem",
                  letterSpacing: "0.34em",
                }}
              >
                ✦  The Ecosystem  ✦
              </span>
              <button
                onClick={() => setOpenBelow(null)}
                data-testid="oml-explore-close"
                style={{
                  color: "rgba(245,236,217,0.5)",
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.58rem",
                  letterSpacing: "0.28em",
                  textTransform: "uppercase",
                }}
              >
                Close
              </button>
            </div>
            <ExploreVisionSection count={count} />
          </div>
        )}

        {step === "arrival" && openBelow === "faqs" && (
          <div ref={faqsAnchorRef} data-testid="oml-faqs-anchor">
            <OmlFaqsSection onClose={() => setOpenBelow(null)} />
          </div>
        )}

        {/* Non-arrival steps · Feb 2026 Michael simplification.
            When the visitor is inside the light-placement flow
            (`intention` → `gift` → `illumination` → `share`), the page
            hides Foundations reveal, Global Alignment and the
            Explore Vision block. Those belong on the arrival hero.
            Once the visitor has committed to placing a light, nothing
            competes with completing the action — no scroll-below
            re-pitch of the wider ecosystem. */}
      </div>

      <FoundingLightModal
        open={showFL}
        onClose={() => setShowFL(false)}
        onJoined={(data) => setFlJoined({ email: data.email, at: new Date().toISOString() })}
      />

      <PostDonationModal
        open={showPostDonation}
        light={light}
        onClose={() => setShowPostDonation(false)}
      />

      {/* Impact Receipt · Feb 22 2026 · Michael. Share-friendly card
          that appears after Stripe confirms the payment. Shows on
          top of the arrival regardless of the globe pulse state so
          anonymous donors (no light-by-session lookup) also see it. */}
      {impactReceipt && (
        <ImpactReceipt
          receipt={impactReceipt}
          onClose={() => setImpactReceipt(null)}
        />
      )}
      {showCirclesSheet && (
        <CirclesOfCareSheet
          lights={amountGbp}
          currencySymbol={CURRENCY_SYMBOL[currency] || "£"}
          amount={amountGbp}
          onConfirm={(payload) => runCheckout(payload)}
          onCancel={() => setShowCirclesSheet(false)}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * Screen 1 — Arrival (cinematic sequence)
 *
 * Six beats orchestrated by the Living Lights Field canvas layer,
 * fading in over ~14 seconds. Nothing else on the screen until the
 * visitor taps the seed light.
 *
 *   T0.0 — Near darkness. One tiny centre light.
 *   T1.5 — "The world does not need another opinion."
 *          "It needs more light."           (fade in, slow)
 *   T4.5 — "What if yours cost just £1?"    (fade in below)
 *   T6.0 — small "tap the light" hint above the seed
 *   Tap  — seed blooms · wave sweeps · thousands of lights emerge
 *          · ambient audio + heartbeat begin
 *          · full copy stack reveals:
 *              headline · CTA · reassurance · count · ticker
 * ────────────────────────────────────────────────────────────── */
function TickerLine({ entry }) {
  if (!entry) return null;
  const secs = entry.seconds_ago || 0;
  const ago =
    secs < 60 ? `${secs} seconds ago`
    : secs < 3600 ? `${Math.floor(secs / 60)} minutes ago`
    : `${Math.floor(secs / 3600)} hours ago`;
  const location = entry.city ? ` in ${entry.city}` : "";
  return (
    <span
      className="italic"
      style={{
        color: TOK.creamReadable,
        fontFamily: '"Cormorant Garamond", serif',
        fontSize: "0.98rem",
      }}
    >
      A light for <span style={{ color: TOK.gold }}>{entry.intention}</span>
      {" was placed"}{location} {ago}.
    </span>
  );
}

function RollingTicker({ entries }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!entries || !entries.length) return;
    const iv = setInterval(() => setIdx((i) => (i + 1) % entries.length), 3400);
    return () => clearInterval(iv);
  }, [entries]);
  if (!entries || !entries.length) return null;
  const cur = entries[idx % entries.length];
  return (
    <div
      className="mt-8 min-h-[1.6em] transition-opacity"
      key={idx}
      data-testid="oml-ticker"
      style={{ animation: "omlFadeIn 900ms ease" }}
    >
      <TickerLine entry={cur} />
    </div>
  );
}

function ArrivalScreen({ onEnter, onJoinFree, onGift, onExplore, onFaqs, onFounding, flJoined, reduceMotion, count, recent, freshLight, freshFaded, onFreshFaded, myPlacedLight, onSeeMyLight }) {
  // Cinematic state machine: seed → waking → living. If we arrived
  // with a freshLight in tow (i.e. Stripe just redirected us back),
  // skip the seed phase entirely so the globe + white pulse are the
  // first thing the user sees.
  const [fieldState, setFieldState] = useState(freshLight ? "living" : "seed");
  // When freshLight is delivered ASYNC after mount (polling
  // resolves the payment), leap the seed-light state machine
  // straight to "living" so the globe replaces the tap-the-light
  // canvas without a manual tap.
  useEffect(() => {
    if (freshLight) setFieldState("living");
  }, [freshLight]);
  // Audio is OFF by default (Michael, Mar 2026: iOS silent-switch
  // muted playback and the score kept re-firing). Visitor taps the
  // 🔊 button to enable, and the preference persists.
  const [soundOn, setSoundOn] = useState(false);
  // Copy timing driven off mount time. Refs so we don't rerender on
  // every tick.
  const [copyBeat, setCopyBeat] = useState(0); // 0 hidden, 1 line1, 2 line2, 3 line3, 4 hint
  useEffect(() => {
    // Feb 2026 v2 review · Michael. The 8.2s pre-tap animation was
    // holding mobile visitors from the practical promise. Halved the
    // beats and added a skippable tap-hint from the very start so
    // anyone impatient can jump straight in.
    //   T0.5 — "The world is being rocked."
    //   T2.0 — "Yet everywhere, unseen carers…"
    //   T3.6 — "It's time we said thank you."
    //   T4.4 — tap-hint reveal
    const t1 = setTimeout(() => setCopyBeat(1),  500);
    const t2 = setTimeout(() => setCopyBeat(2), 2000);
    const t3 = setTimeout(() => setCopyBeat(3), 3600);
    const t4 = setTimeout(() => setCopyBeat(4), 4400);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
  }, []);

  const awaken = () => {
    if (fieldState !== "seed") return;
    setFieldState("waking");
    setTimeout(() => setFieldState("living"), 2200);
  };

  const toggleSound = () => {
    if (soundOn) {
      try { openingAudio.stop(); } catch (_) {}
      setSoundOn(false);
    } else {
      try {
        openingAudio.start({ targetVolume: 0.55 });
        openingAudio.chime(0.8);
      } catch (_) {}
      setSoundOn(true);
    }
  };

  const awoken = fieldState !== "seed";

  // Feb 27 2026 · Michael. The three fixed chips (See my light /
  // Share / Sound Off) at top:16 were still floating over the
  // "Six doorways" heading and the Explore section on mobile.
  // Fade + disable them once the visitor scrolls past the arrival
  // hero so they read below-the-fold content cleanly.
  const [chipsFaded, setChipsFaded] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onScroll = () => setChipsFaded(window.scrollY > 320);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const chipFadeStyle = {
    opacity: chipsFaded ? 0 : 1,
    pointerEvents: chipsFaded ? "none" : "auto",
    transition: "opacity 350ms ease",
  };

  return (
    <>
      {/* Full-screen cinematic canvas — sits BEHIND the copy stack.
          Positioned absolutely against the flow wrapper. */}
      <div
        className="absolute inset-x-0"
        style={{
          top: 0,
          height: "100vh",
          pointerEvents: awoken ? "none" : "auto",
          // Feb 25 2026 · Michael: "Stars are distracting" on the
          // OML page. After awakening we dim the whole living field
          // and mask it so the amber lights linger around the globe
          // (top ~55% of the viewport) and fade to transparent
          // where the reading copy sits. Pre-awakening this stays
          // fully bright so the arrival moment feels alive.
          opacity: awoken ? 0.45 : 1,
          transition: "opacity 900ms ease",
          WebkitMaskImage: awoken
            ? "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 45%, rgba(0,0,0,0.35) 68%, rgba(0,0,0,0) 82%)"
            : undefined,
          maskImage: awoken
            ? "linear-gradient(to bottom, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 45%, rgba(0,0,0,0.35) 68%, rgba(0,0,0,0) 82%)"
            : undefined,
        }}
      >
        <LivingLightsField
          state={fieldState}
          reduceMotion={reduceMotion}
          onSeedTap={awaken}
          recentPool={recent}
          centerYRatio={awoken ? 0.78 : 0.5}
        />
      </div>

      {/* ── Pre-tap copy ─────────────────────────────────────
          A tiny, elegant stack sitting ABOVE the seed light. The
          canvas centres its seed vertically inside the wrapper, so
          we position this stack above centre with a bit of pt. */}
      {!awoken && (
        <div
          className="relative z-10 pointer-events-none flex flex-col items-center gap-4 max-w-md mx-auto"
          style={{ marginTop: "12vh", minHeight: "60vh", justifyContent: "flex-start" }}
        >
          <div
            className="italic transition-opacity duration-1000"
            style={{
              opacity: copyBeat >= 1 ? 1 : 0,
              color: TOK.cream,
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: "clamp(1.35rem, 4.8vw, 1.9rem)",
              lineHeight: 1.35,
              fontWeight: 300,
            }}
            data-testid="oml-arrival-line-1"
          >
            The world is being rocked.
          </div>
          <div
            className="italic transition-opacity duration-1000"
            style={{
              opacity: copyBeat >= 2 ? 1 : 0,
              color: TOK.creamReadable,
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: "clamp(1.1rem, 3.8vw, 1.45rem)",
              lineHeight: 1.4,
            }}
            data-testid="oml-arrival-line-2"
          >
            Yet everywhere, unseen carers are quietly
            <br />
            supporting and offering their love and care.
          </div>
          <div
            className="italic transition-opacity duration-1000"
            style={{
              opacity: copyBeat >= 3 ? 1 : 0,
              color: TOK.cream,
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: "clamp(1.2rem, 4.2vw, 1.6rem)",
              lineHeight: 1.35,
              fontWeight: 300,
            }}
            data-testid="oml-arrival-line-3"
          >
            It’s time we said <span style={{ color: TOK.gold }}>thank you.</span>
          </div>
          <div
            className="uppercase transition-opacity duration-1000"
            style={{
              opacity: copyBeat >= 4 ? 1 : 0,
              color: TOK.goldSoft,
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.62rem",
              letterSpacing: "0.36em",
              marginTop: "16vh",
            }}
            data-testid="oml-tap-hint"
          >
            ✦  Tap the light  ✦
          </div>
          {/* Skip → · Feb 2026 v2 review · Michael asked for the
              animation to be skippable. Sits low and quiet, but is
              tappable from the very first second so an impatient
              mobile visitor can jump straight into the globe. */}
          <button
            type="button"
            onClick={awaken}
            data-testid="oml-skip-intro"
            className="pointer-events-auto mt-6 uppercase"
            style={{
              background: "transparent",
              color: TOK.creamMist,
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.56rem",
              letterSpacing: "0.32em",
              padding: "8px 14px",
              borderBottom: `1px dashed ${TOK.goldMist}`,
            }}
          >
            Skip →
          </button>
        </div>
      )}

      {/* ── Post-tap copy ─────────────────────────────────────
          Post-tap simplification (Feb 10 2026): the arrival now
          presents two prominent doorways — Place a Light and Become
          a Founding Light — with a small tertiary row of secondary
          actions (Gift · Explore our apps · FAQs). Everything else
          (long copy, count, ticker) lives one scroll below so the
          first choice feels uncluttered.  */}
      {awoken && (
        <>
          {/* "See my light" — subtle chip, top-left, only shown for
              returning contributors so they can revisit their
              illumination without breaking the globe-first arrival. */}
          {myPlacedLight && !freshLight && (
            <button
              type="button"
              onClick={onSeeMyLight}
              data-testid="oml-see-my-light"
              className="fixed top-4 left-4 z-20 rounded-full flex items-center gap-2 px-3 py-2"
              style={{
                background: "rgba(19,31,56,0.55)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                border: `1px solid ${TOK.gold}`,
                color: TOK.gold,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.58rem",
                letterSpacing: "0.24em",
                textTransform: "uppercase",
                fontWeight: 600,
                ...chipFadeStyle,
              }}
            >
              <span style={{ fontSize: "0.9rem", lineHeight: 1 }}>◐</span>
              See my light · #{Number(myPlacedLight.number || 0).toLocaleString("en-GB")}
            </button>
          )}
          {/* Discreet sound toggle — top-right corner. Audio is OFF
              by default because iOS Safari's silent switch mutes
              Web Audio, and Michael reported previously that music
              starting automatically felt intrusive. */}
          <button
            onClick={toggleSound}
            data-testid="oml-sound-toggle"
            aria-label={soundOn ? "Turn sound off" : "Turn sound on"}
            className="fixed top-4 right-4 z-20 rounded-full flex items-center gap-2 px-3 py-2"
            style={{
              background: "rgba(19,31,56,0.55)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              border: `1px solid ${soundOn ? TOK.gold : TOK.goldMist}`,
              color: soundOn ? TOK.gold : TOK.goldSoft,
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.6rem",
              letterSpacing: "0.24em",
              textTransform: "uppercase",
              fontWeight: 600,
              ...chipFadeStyle,
            }}
          >
            <span style={{ fontSize: "0.95rem", lineHeight: 1 }}>{soundOn ? "♪" : "♪"}</span>
            {soundOn ? "Sound On" : "Sound Off"}
          </button>
          {/* Share — Feb 22 2026 · Michael. A clear share affordance
              on the arrival screen so anyone can pass this on before
              (or without) placing their light. Uses navigator.share
              when available (mobile) and falls back to copying the
              link + a small toast. */}
          <button
            onClick={async () => {
              const url = typeof window !== "undefined" ? window.location.origin + "/?brand=oml" : "";
              const text = "One Million Lights — a global thank-you to the people quietly holding the world together.";
              try {
                if (navigator.share) {
                  await navigator.share({ title: "One Million Lights", text, url });
                } else if (navigator.clipboard) {
                  await navigator.clipboard.writeText(`${text} · ${url}`);
                  // Small unobtrusive toast via alert-fallback avoided;
                  // instead flash the button briefly by using a data attr.
                  const el = document.querySelector('[data-testid="oml-share-arrival"]');
                  if (el) {
                    el.setAttribute("data-copied", "1");
                    setTimeout(() => el.removeAttribute("data-copied"), 1400);
                  }
                }
              } catch (_) { /* user cancelled */ }
            }}
            data-testid="oml-share-arrival"
            aria-label="Share One Million Lights"
            className="fixed top-4 right-32 z-20 rounded-full flex items-center gap-2 px-3 py-2"
            style={{
              background: "rgba(19,31,56,0.55)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              border: `1px solid ${TOK.goldMist}`,
              color: TOK.goldSoft,
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.6rem",
              letterSpacing: "0.24em",
              textTransform: "uppercase",
              fontWeight: 600,
              ...chipFadeStyle,
            }}
          >
            <span aria-hidden style={{ fontSize: "0.85rem", lineHeight: 1 }}>↗</span>
            Share
          </button>
          <div
            className="relative z-10 flex flex-col items-center max-w-md mx-auto px-1"
            style={{
              // Push the whole arrival stack below the iOS status
              // bar + any in-app browser chrome (Michael's Emergent
              // pill was overlapping the counter). Safe-area env()
              // handles the notch, and the min 90px keeps the
              // globe clear on browsers without the pill too.
              marginTop: "max(90px, calc(env(safe-area-inset-top, 0px) + 6vh))",
              animation: "omlFadeIn 1100ms ease",
            }}
          >
            {/* ── Living Globe — the emotional centre ──────────────
                Feb 11 2026 · Michael's Master Prompt. Every verified
                light appears in warm gold; a fresh contribution
                emerges in brilliant white then softens into gold as
                it "joins the collective". The globe sits above the
                doorways so it is the first thing seen and the
                emotional anchor of every decision below. */}
            <LivingGlobe
              height={Math.min(460, Math.max(320, window.innerHeight * 0.5))}
              reduceMotion={reduceMotion}
              freshLight={freshLight}
              onFreshFaded={onFreshFaded}
            />

            {/* Feb 27 2026 · Michael's rolling autocue — a single-
                line ticker directly beneath the globe. "Michael just
                placed 3 lights in England." Blends real 24 h
                placements ahead of a seed pool of 51 across Europe /
                Americas / Asia / Oceania / Africa. */}
            {!freshLight && (
              <div className="mt-3" data-testid="oml-ticker-mount">
                <LightsPlacementTicker />
              </div>
            )}

            {/* Phase 2 · While the fresh-light pulse is playing we
                replace the everyday hero copy with the master-
                prompt's dedicated confirmation copy: "Your light is
                shining." + "You are now part of something bigger."
                After the fade-to-gold completes we transition into
                the share invitation. */}
            {freshLight && !freshFaded && (
              <div
                className="mt-6 text-center max-w-md"
                data-testid="oml-your-light-copy"
                style={{ animation: "omlFadeIn 900ms ease" }}
              >
                <div
                  className="italic leading-tight"
                  style={{
                    color: TOK.cream,
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: "clamp(1.5rem, 5vw, 2rem)",
                    fontWeight: 300,
                    textShadow: "0 0 30px rgba(255,255,255,0.35)",
                  }}
                >
                  {(freshLight.count || 1) > 1
                    ? `Your ${freshLight.count} lights are shining.`
                    : "Your light is shining."}
                </div>
                <p
                  className="mt-3 italic"
                  style={{
                    color: TOK.creamMist,
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: "clamp(0.98rem, 3vw, 1.15rem)",
                  }}
                >
                  You are now part of something bigger.
                </p>
              </div>
            )}
            {freshLight && freshFaded && (
              <div
                className="mt-6 text-center max-w-md w-full"
                data-testid="oml-your-light-share"
                style={{ animation: "omlFadeIn 900ms ease" }}
              >
                <div
                  className="italic leading-tight"
                  style={{
                    color: TOK.cream,
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: "clamp(1.35rem, 4.6vw, 1.8rem)",
                    fontWeight: 300,
                  }}
                >
                  Your light is shining.
                  <br />
                  <span style={{ color: TOK.gold }}>Who will you invite next?</span>
                </div>
                <div className="mt-6 flex flex-col gap-3">
                  <button
                    type="button"
                    onClick={onGift}
                    data-testid="oml-share-the-light"
                    className="rounded-full transition-all w-full"
                    style={{
                      background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
                      border: "1px solid #f5e0c5",
                      color: "#1a0f05",
                      fontFamily: '"Manrope", sans-serif',
                      fontSize: "0.86rem",
                      fontWeight: 700,
                      letterSpacing: "0.22em",
                      textTransform: "uppercase",
                      padding: "18px 26px",
                      boxShadow: "0 0 44px rgba(224,169,122,0.4), 0 8px 24px rgba(0,0,0,0.35)",
                    }}
                  >
                    Share the Light
                  </button>
                  <button
                    type="button"
                    onClick={() => { onFreshFaded?.(); window.location.assign("/?brand=oml"); }}
                    data-testid="oml-share-return"
                    className="rounded-full transition-all w-full"
                    style={{
                      background: "transparent",
                      border: `1px solid ${TOK.goldMist}`,
                      color: TOK.creamMist,
                      fontFamily: '"Manrope", sans-serif',
                      fontSize: "0.62rem",
                      letterSpacing: "0.28em",
                      textTransform: "uppercase",
                      padding: "14px 22px",
                    }}
                  >
                    Return to the movement
                  </button>
                </div>
              </div>
            )}

            {/* Headline — CARE reposition (Feb 2026). Practical
                promise now lives right under the emotional headline
                so mobile visitors understand the gift without
                scrolling. Feb 2026 v2 review · Michael. */}
            {!freshLight && (
              <>
            <div
              className="uppercase text-center mt-6"
              style={{
                color: TOK.goldSoft,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.62rem",
                letterSpacing: "0.36em",
              }}
              data-testid="oml-hero-eyebrow"
            >
              ✦  One Million Lights  ✦
            </div>
            <div
              className="italic leading-tight text-center mt-3"
              style={{
                color: TOK.cream,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "clamp(1.35rem, 4.8vw, 1.9rem)",
                fontWeight: 300,
              }}
              data-testid="oml-hero-headline"
            >
              A global thank-you to the people
              <br />
              <span style={{ color: TOK.gold }}>quietly holding the world together.</span>
            </div>
            {/* The practical promise — the review's #1 ask. Sits
                directly under the headline in a size that reads on
                a phone (base ~1.05rem, up to 1.2rem on larger
                screens). Explains exactly what a £1 light does. */}
            <p
              className="mt-5 text-center max-w-md"
              style={{
                color: TOK.cream,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "clamp(1.05rem, 3.4vw, 1.2rem)",
                lineHeight: 1.5,
              }}
              data-testid="oml-hero-promise"
            >
              Give <span style={{ color: TOK.gold, fontWeight: 500 }}>£1</span>.
              Help a carer receive Soulumo Foundations —
              a small place to pause, breathe and feel cared for.
            </p>
            <p
              className="mt-3 italic text-center max-w-md"
              style={{
                color: TOK.creamReadable,
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "clamp(0.95rem, 3vw, 1.05rem)",
                lineHeight: 1.5,
              }}
              data-testid="oml-hero-recipients"
            >
              We work with carers and community partners to reach
              carers and the people they care for.
            </p>
              </>
            )}

            {/* Primary action — Place a Light. This is the ONLY
                thing on the first "page" after the button; anything
                else lives in the four chip-reveals below. Feb 13
                2026 · Michael. */}
            {!freshLight && (
            <div className="mt-7 w-full flex flex-col items-center">
              <button
                onClick={onEnter}
                data-testid="oml-enter"
                className="rounded-full transition-all w-full"
                style={{
                  background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
                  border: "1px solid #f5e0c5",
                  color: "#1a0f05",
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.98rem",
                  fontWeight: 700,
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  padding: "22px 30px",
                  boxShadow: "0 0 60px rgba(224,169,122,0.55), 0 8px 24px rgba(0,0,0,0.35)",
                }}
              >
                Place a Light — £1
              </button>
            </div>
            )}

            {/* Micro-reassurance removed Feb 13 2026 · Michael asked
                the first page to end cleanly after the button. All
                supporting copy now lives inside the four chip
                reveals below. */}

            {/* Secondary options row · Gift · Founding · FAQs.
                Feb 2026 v2 review · Michael. Removed the "Apps" chip
                — the six-doorway ecosystem was reading as a second
                marketing site inside the campaign. A small
                "Explore the wider ecosystem →" text link now sits
                right at the bottom of the page. */}
            {!freshLight && (
            <div
              className="mt-16 w-full grid grid-cols-3 gap-2"
              data-testid="oml-secondary-nav"
            >
              <button
                onClick={onGift}
                data-testid="oml-nav-gift"
                className="rounded-full transition-all"
                style={{
                  background: "transparent",
                  border: `1px solid ${TOK.goldMist}`,
                  color: TOK.goldSoft,
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.66rem",
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  padding: "14px 4px",
                  minHeight: 48,
                }}
              >
                What is the gift?
              </button>
              <button
                onClick={onFounding}
                data-testid="oml-nav-founding"
                className="rounded-full transition-all"
                style={{
                  background: "transparent",
                  border: `1px solid ${TOK.goldMist}`,
                  color: TOK.goldSoft,
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.66rem",
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  padding: "14px 4px",
                  minHeight: 48,
                }}
              >
                Founding
              </button>
              <button
                onClick={onFaqs}
                data-testid="oml-nav-faqs"
                className="rounded-full transition-all"
                style={{
                  background: "transparent",
                  border: `1px solid ${TOK.goldMist}`,
                  color: TOK.goldSoft,
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.66rem",
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  padding: "14px 4px",
                  minHeight: 48,
                }}
              >
                FAQs
              </button>
            </div>
            )}

            {/* Three short answers to the questions visitors ask
                most. Feb 2026 v2 review · Michael. Fuller answers
                still live inside the FAQs chip. */}
            {!freshLight && (
              <div
                className="mt-10 w-full max-w-md space-y-3"
                data-testid="oml-hero-mini-answers"
              >
                {[
                  {
                    q: "Where does my £1 go?",
                    a: "Direct to opening Soulumo Foundations for one carer. We show a public running total and honest updates as the movement grows.",
                  },
                  {
                    q: "How are carers chosen?",
                    a: "Anyone can nominate a carer, or we route lights through community and charity partners who work with unseen carers.",
                  },
                  {
                    q: "What actually happens?",
                    a: "Your light lands on the globe. A carer receives Foundations — daily morning + night audio, resets, and the signature installations.",
                  },
                ].map((m, i) => (
                  <div
                    key={i}
                    className="text-left rounded-2xl px-4 py-3"
                    style={{
                      background: "rgba(245,236,217,0.045)",
                      border: `1px solid ${TOK.goldMist}`,
                    }}
                    data-testid={`oml-hero-mini-${i}`}
                  >
                    <div
                      className="uppercase"
                      style={{
                        color: TOK.goldSoft,
                        fontFamily: '"Manrope", sans-serif',
                        fontSize: "0.6rem",
                        letterSpacing: "0.24em",
                      }}
                    >
                      {m.q}
                    </div>
                    <p
                      className="mt-1 italic"
                      style={{
                        color: TOK.cream,
                        fontFamily: '"Cormorant Garamond", serif',
                        fontSize: "clamp(0.95rem, 3vw, 1.02rem)",
                        lineHeight: 1.45,
                      }}
                    >
                      {m.a}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {/* Tiny footer link — the "Explore other apps" doorway
                previously lived in the four-chip row. Now it's a
                quiet aside at the bottom for anyone curious. */}
            {!freshLight && (
              <button
                type="button"
                onClick={onExplore}
                data-testid="oml-nav-explore"
                className="mt-10 uppercase"
                style={{
                  background: "transparent",
                  color: TOK.creamMist,
                  fontFamily: '"Manrope", sans-serif',
                  fontSize: "0.58rem",
                  letterSpacing: "0.28em",
                  padding: "6px 4px",
                  borderBottom: `1px dashed ${TOK.goldMist}`,
                }}
              >
                Explore the wider ecosystem →
              </button>
            )}

            {/* Old "Every £1 · one thank-you..." microcopy removed Feb
                13 2026 — Michael asked the first page to end at the
                button and the four options row. */}
          </div>
        </>
      )}
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * Screen 2 — Intention picker
 * ────────────────────────────────────────────────────────────── */
function IntentionScreen({ intentions, intention, setIntention, ownWord, setOwnWord, onContinue, onBack, reduceMotion }) {
  const canContinue = intention === "__own__" ? ownWord.trim().length > 0 : !!intention;
  return (
    <>
      {/* Step indicator — makes it clear this is the payment wizard,
          not a separate marketing page. Michael, Mar 2026: the old
          InstallOrb here read as "an old homepage screen" — removed. */}
      <div
        className="mt-6 uppercase text-center"
        style={{
          color: TOK.goldSoft,
          fontFamily: '"Manrope", sans-serif',
          fontSize: "0.6rem",
          letterSpacing: "0.36em",
        }}
      >
        Step 1 of 2 · Your word
      </div>

      <h2
        className="mt-4 max-w-md mx-auto leading-tight"
        style={{
          color: TOK.cream,
          fontFamily: '"Cormorant Garamond", serif',
          fontStyle: "italic",
          fontSize: "clamp(1.5rem, 5vw, 2.1rem)",
          fontWeight: 300,
        }}
        data-testid="oml-intention-heading"
      >
        Choose the light you want to place in the world.
      </h2>

      <div className="mt-8 flex flex-wrap justify-center gap-2 max-w-md">
        {intentions.map((word) => (
          <IntentionChip
            key={word}
            word={word}
            active={intention === word}
            onClick={() => { setIntention(word); setOwnWord(""); }}
          />
        ))}
        <IntentionChip
          word="My own word"
          active={intention === "__own__"}
          onClick={() => setIntention("__own__")}
        />
      </div>

      {intention === "__own__" && (
        <input
          value={ownWord}
          onChange={(e) => setOwnWord(e.target.value.slice(0, 60))}
          data-testid="oml-own-word"
          placeholder="Your word"
          className="mt-4 bg-transparent border-b text-center italic px-3 py-2 outline-none"
          style={{
            color: TOK.cream,
            borderColor: TOK.goldMist,
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "1.2rem",
            minWidth: 220,
          }}
          autoFocus
        />
      )}

      <div className="mt-10 flex items-center gap-4">
        <button
          onClick={onBack}
          className="text-[0.72rem] uppercase tracking-[0.24em]"
          style={{ color: TOK.creamReadable, fontFamily: '"Manrope", sans-serif' }}
        >
          ← Back
        </button>
        <button
          onClick={onContinue}
          disabled={!canContinue}
          data-testid="oml-continue-to-gift"
          className="rounded-full transition-all"
          style={{
            padding: "14px 28px",
            background: canContinue ? "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)" : "rgba(245,236,217,0.06)",
            border: canContinue ? "1px solid #f5e0c5" : `1px solid ${TOK.goldMist}`,
            color: canContinue ? "#1a0f05" : TOK.creamMist,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.8rem",
            fontWeight: 600,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            boxShadow: canContinue ? "0 0 30px rgba(224,169,122,0.28)" : "none",
            opacity: canContinue ? 1 : 0.6,
            cursor: canContinue ? "pointer" : "not-allowed",
          }}
        >
          Continue
        </button>
      </div>
    </>
  );
}

function IntentionChip({ word, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`oml-intention-${word.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className="rounded-full transition-all"
      style={{
        padding: "10px 20px",
        background: active ? "rgba(224,169,122,0.22)" : "rgba(245,236,217,0.05)",
        border: `1px solid ${active ? TOK.gold : TOK.goldMist}`,
        color: TOK.cream,
        fontFamily: '"Cormorant Garamond", serif',
        fontStyle: "italic",
        fontSize: "1.15rem",
        letterSpacing: "0.02em",
        boxShadow: active ? "0 0 20px rgba(224,169,122,0.22)" : "none",
      }}
    >
      {word}
    </button>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * Screen 3 — £1 place-your-light (preview mode)
 * ────────────────────────────────────────────────────────────── */
function GiftScreen({ finalIntention, displayName, setDisplayName, reserving, error, onPlace, onBack, count, amountGbp, setAmountGbp, currency, setCurrency, reduceMotion }) {
  const AMOUNTS = CURRENCY_LADDERS[currency] || CURRENCY_LADDERS.GBP;
  const SYM = CURRENCY_SYMBOL[currency] || "£";
  // Feb 2026 v2 review · Michael. Currency switcher is a small,
  // quiet chip beside the amount pills. Manual override always wins.
  const [showCurrencies, setShowCurrencies] = useState(false);
  // Feb 22 2026 · Michael. Choose Your Own Amount — a 5th pill that
  // reveals a small numeric input. Values are clamped 1..999 and
  // still strict 1:1 (each unit = Foundations for one person).
  const [customMode, setCustomMode] = useState(false);
  const [customInput, setCustomInput] = useState("");
  const isPresetTier = AMOUNTS.includes(amountGbp);
  const showingCustom = customMode || !isPresetTier;
  return (
    <>
      {/* Step indicator — Mar 2026: replaced the large "building"
          orb here (which read as marketing rather than checkout). */}
      <div
        className="mt-6 uppercase text-center"
        style={{
          color: TOK.goldSoft,
          fontFamily: '"Manrope", sans-serif',
          fontSize: "0.6rem",
          letterSpacing: "0.36em",
        }}
      >
        Step 2 of 2 · Your gift
      </div>

      {/* Chosen word — prominent, beautifully framed. */}
      <div
        className="mt-4"
        style={{
          color: TOK.cream,
          fontFamily: '"Cormorant Garamond", serif',
        }}
      >
        <div
          className="text-[0.72rem] uppercase tracking-[0.28em]"
          style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
        >
          Your light
        </div>
        <div
          className="mt-2 font-light"
          style={{ fontSize: "clamp(2.2rem, 7vw, 3rem)", color: TOK.gold }}
        >
          {finalIntention}
        </div>
      </div>

      {/* Name input — same word contrast. */}
      <input
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value.slice(0, 60))}
        data-testid="oml-display-name"
        placeholder="Your name (appears on your Founding Light)"
        className="mt-8 bg-transparent border-b text-center italic px-3 py-3 outline-none w-full max-w-sm"
        style={{
          color: TOK.cream,
          borderColor: TOK.goldMist,
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: "1.08rem",
        }}
      />

      {/* Amount pills — reframed as "how far would you like your
          light to travel?" per Michael's brief. £1 remains the
          anchor / default, so nothing feels like an upsell. Feb
          2026 v2 review · Michael fixed the arithmetic: strict 1:1
          — every £1/€1/$1 opens Foundations for one person. */}
      <div className="mt-10 max-w-md mx-auto">
        <div className="flex items-baseline justify-between gap-3">
          <div
            className="italic"
            style={{
              color: TOK.cream,
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: "clamp(1.1rem, 3.6vw, 1.35rem)",
            }}
          >
            How far would you like your light to travel?
          </div>
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowCurrencies((v) => !v)}
              data-testid="oml-currency-switcher"
              className="uppercase whitespace-nowrap"
              style={{
                background: "transparent",
                border: `1px solid ${TOK.goldMist}`,
                color: TOK.goldSoft,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.62rem",
                letterSpacing: "0.24em",
                padding: "6px 10px",
                borderRadius: 999,
              }}
            >
              {SYM} · Change
            </button>
            {showCurrencies && (
              <div
                className="absolute right-0 mt-2 z-20 rounded-2xl"
                data-testid="oml-currency-menu"
                style={{
                  background: "rgba(19,31,56,0.95)",
                  border: `1px solid ${TOK.goldMist}`,
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                  minWidth: 140,
                  padding: 6,
                  boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
                }}
              >
                {["GBP", "USD", "EUR"].map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => { setCurrency(c); setShowCurrencies(false); }}
                    data-testid={`oml-currency-${c.toLowerCase()}`}
                    className="w-full text-left uppercase rounded-xl"
                    style={{
                      background: c === currency ? "rgba(224,169,122,0.18)" : "transparent",
                      color: c === currency ? TOK.gold : TOK.cream,
                      fontFamily: '"Manrope", sans-serif',
                      fontSize: "0.66rem",
                      letterSpacing: "0.24em",
                      padding: "10px 12px",
                    }}
                  >
                    {CURRENCY_SYMBOL[c]}  ·  {c}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 flex flex-col gap-2 items-stretch">
          {AMOUNTS.map((a) => {
            const active = amountGbp === a && !customMode;
            // Strict 1:1 impact copy — one gift funds N Foundations.
            const impact = a === 1
              ? "Foundations for 1 person"
              : `Foundations for ${a} people`;
            return (
              <button
                key={a}
                type="button"
                onClick={() => { setCustomMode(false); setAmountGbp(a); }}
                data-testid={`oml-amount-${a}`}
                aria-pressed={active}
                className="rounded-2xl text-left"
                style={{
                  padding: "14px 20px",
                  background: active ? "rgba(224,169,122,0.22)" : "rgba(245,236,217,0.045)",
                  border: `1px solid ${active ? TOK.gold : TOK.goldMist}`,
                  color: TOK.cream,
                  fontFamily: '"Manrope", sans-serif',
                  boxShadow: active ? "0 0 22px rgba(224,169,122,0.28)" : "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <span style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: "1.35rem",
                  fontWeight: 500,
                  color: active ? TOK.gold : TOK.cream,
                }}>
                  {SYM}{a}
                </span>
                <span
                  className="italic text-right"
                  style={{
                    color: active ? TOK.cream : TOK.creamReadable,
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: "1rem",
                    flex: 1,
                  }}
                >
                  {impact}
                </span>
              </button>
            );
          })}
          {/* Choose Your Own Amount — Feb 22 2026 · Michael. The
              donor can enter any whole-currency amount from 1 to 999.
              Value stays strict 1:1 so the impact copy reads
              "Foundations for N people" using the same rules. */}
          <div
            data-testid="oml-amount-custom-wrap"
            className="rounded-2xl"
            style={{
              padding: showingCustom ? "12px 16px" : "14px 20px",
              background: showingCustom ? "rgba(224,169,122,0.18)" : "rgba(245,236,217,0.045)",
              border: `1px solid ${showingCustom ? TOK.gold : TOK.goldMist}`,
              boxShadow: showingCustom ? "0 0 22px rgba(224,169,122,0.24)" : "none",
            }}
          >
            {!showingCustom ? (
              <button
                type="button"
                onClick={() => { setCustomMode(true); setCustomInput(""); }}
                data-testid="oml-amount-custom"
                aria-pressed={false}
                className="w-full text-left flex items-center justify-between gap-3"
              >
                <span style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: "1.35rem",
                  fontWeight: 500,
                  color: TOK.cream,
                }}>
                  {SYM}____
                </span>
                <span
                  className="italic text-right"
                  style={{
                    color: TOK.creamReadable,
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: "1rem",
                    flex: 1,
                  }}
                >
                  Choose your own amount
                </span>
              </button>
            ) : (
              <div className="flex items-center gap-3">
                <span style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontSize: "1.35rem",
                  fontWeight: 500,
                  color: TOK.gold,
                }}>
                  {SYM}
                </span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={999}
                  step={1}
                  value={customInput}
                  onChange={(e) => {
                    // Feb 22 · Michael's v3 review: clamp and reflect
                    // the clamped value back into the visible input
                    // so the user never sees "1500" while the CTA
                    // reads "£150". Strip non-digits, clamp to
                    // 1..999, and always show what will actually
                    // be charged.
                    let raw = e.target.value.replace(/[^0-9]/g, "");
                    if (raw === "") {
                      setCustomInput("");
                      return;
                    }
                    let n = parseInt(raw, 10);
                    if (Number.isNaN(n)) {
                      setCustomInput("");
                      return;
                    }
                    if (n > 999) n = 999;
                    if (n < 1) n = 1;
                    setCustomInput(String(n));
                    setAmountGbp(n);
                  }}
                  placeholder="e.g. 42"
                  data-testid="oml-amount-custom-input"
                  autoFocus
                  className="flex-1 min-w-0 rounded-lg bg-transparent"
                  style={{
                    color: TOK.cream,
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: "1.25rem",
                    fontWeight: 500,
                    border: `1px solid ${TOK.goldMist}`,
                    padding: "8px 12px",
                    outline: "none",
                  }}
                />
                <span
                  className="italic whitespace-nowrap"
                  style={{
                    color: TOK.creamReadable,
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: "0.95rem",
                  }}
                >
                  Foundations for {(() => {
                    const n = parseInt(customInput, 10);
                    if (!Number.isFinite(n) || n < 1) return "…";
                    return n === 1 ? "1 person" : `${n} people`;
                  })()}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Primary CTA — big, gold-filled, warm glow. */}
      <button
        onClick={onPlace}
        disabled={reserving}
        data-testid="oml-place-light"
        className="mt-8 rounded-full transition-all"
        style={{
          background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
          border: "1px solid #f5e0c5",
          color: "#1a0f05",
          fontFamily: '"Manrope", sans-serif',
          fontSize: "0.86rem",
          fontWeight: 700,
          letterSpacing: "0.20em",
          textTransform: "uppercase",
          padding: "18px 34px",
          boxShadow: "0 0 44px rgba(224,169,122,0.4), 0 8px 24px rgba(0,0,0,0.35)",
          cursor: reserving ? "not-allowed" : "pointer",
          opacity: reserving ? 0.7 : 1,
        }}
      >
        {reserving
          ? "Placing your light…"
          : amountGbp === 1
            ? `Place my light · ${SYM}${amountGbp}`
            : `Place ${amountGbp} lights · ${SYM}${amountGbp}`}
      </button>

      {error && (
        <p
          className="mt-4 text-[0.9rem] italic"
          style={{ color: "rgba(224,122,106,0.95)", fontFamily: '"Cormorant Garamond", serif' }}
        >
          {error}
        </p>
      )}

      {/* Preview state chip — quiet, at the bottom of the section.
          Removed automatically the moment payments go live (per brief). */}
      <span
        className="mt-8 inline-block rounded-full px-3 py-[3px] text-[0.5rem] uppercase tracking-[0.28em]"
        style={{
          background: "rgba(224,169,122,0.06)",
          border: `1px solid ${TOK.goldMist}`,
          color: TOK.creamMist,
          fontFamily: '"Manrope", sans-serif',
        }}
      >
        Preview · no gift taken
      </span>

      <button
        onClick={onBack}
        className="mt-6 text-[0.72rem] uppercase tracking-[0.24em]"
        style={{ color: TOK.creamReadable, fontFamily: '"Manrope", sans-serif' }}
      >
        ← Change my word
      </button>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * Screen 4 — Illumination (reveal)
 * ────────────────────────────────────────────────────────────── */
function IlluminationScreen({ light, onShare, onNew, reduceMotion }) {
  return (
    <>
      <div className="mt-2" style={{ pointerEvents: "none" }}>
        <div style={{ width: 260, height: 260 }}>
          <InstallOrb state="complete" size={260} />
        </div>
      </div>
      <div
        className="mt-8 text-[0.52rem] uppercase tracking-[0.36em]"
        style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
      >
        Founding Light · #{Number(light.number || 0).toLocaleString("en-GB")}
      </div>
      <h2
        className="mt-2 font-light"
        style={{
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: "clamp(2rem, 6vw, 2.8rem)",
          color: TOK.cream,
        }}
      >
        {light.display_name || "Your light"}
      </h2>
      <div
        className="mt-2 italic"
        style={{
          fontFamily: '"Cormorant Garamond", serif',
          color: TOK.gold,
          fontSize: "clamp(1.2rem, 4vw, 1.6rem)",
        }}
      >
        {light.intention}
      </div>

      <p
        className="mt-8 max-w-md leading-relaxed"
        style={{
          color: TOK.cream,
          fontFamily: '"Cormorant Garamond", serif',
          fontStyle: "italic",
          fontSize: "clamp(1.1rem, 3.4vw, 1.32rem)",
        }}
      >
        You placed <span style={{ color: TOK.gold }}>{light.intention}</span> into the world.
      </p>
      <p
        className="mt-3 max-w-md leading-relaxed"
        style={{
          color: TOK.creamReadable,
          fontFamily: '"Manrope", sans-serif',
          fontSize: "0.98rem",
          lineHeight: 1.55,
        }}
      >
        Your light is now part of something larger — and your gift will help
        someone else begin finding theirs.
      </p>

      <div className="mt-8 flex items-center gap-4">
        <button
          onClick={onShare}
          data-testid="oml-open-share"
          className="rounded-full px-6 py-3"
          style={{
            background: "rgba(224,169,122,0.16)",
            border: `1px solid ${TOK.gold}`,
            color: TOK.cream,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.72rem",
            letterSpacing: "0.28em",
            textTransform: "uppercase",
          }}
        >
          Share my light
        </button>
        <button
          onClick={onNew}
          className="text-[0.62rem] uppercase tracking-[0.28em]"
          style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}
        >
          Change my word
        </button>
      </div>

      {light.preview && (
        <p
          className="mt-6 text-[0.55rem] uppercase tracking-[0.36em]"
          style={{ color: TOK.creamMist, fontFamily: '"Manrope", sans-serif' }}
        >
          Preview number · no gift taken
        </p>
      )}
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * Screen 5 — Share card
 * A print-ready portrait card the user can screenshot or save.
 * A proper server-side PNG rendered by the same portrait pipeline
 * as the auto-Reel is a next step; for now the on-screen card is
 * the shareable artefact.
 * ────────────────────────────────────────────────────────────── */
function ShareScreen({ light, onBack }) {
  const cardRef = useRef(null);
  const siteUrl = "1millionlights.app";
  const pngUrl = `${API}/api/oml/share-card/${light.id}.png`;
  const shareText = `I placed my light.\nMy word is ${light.intention}.\nWhat will yours be? · ${siteUrl}`;
  const [downloading, setDownloading] = useState(false);
  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "One Million Lights", text: shareText, url: `https://${siteUrl}` });
        return;
      } catch (_) { /* user cancelled */ }
    }
    try {
      await navigator.clipboard.writeText(shareText);
    } catch (_) { /* fine */ }
  };
  const downloadPng = async () => {
    setDownloading(true);
    try {
      const res = await fetch(pngUrl, { cache: "no-store" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `one-million-lights-${light.number || "founding"}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (_) {
      // Fallback — open in new tab so user can long-press-save on iOS
      window.open(pngUrl, "_blank");
    } finally {
      setDownloading(false);
    }
  };
  const intentionDisplay = String(light.intention || "").toUpperCase().slice(0, 14);
  return (
    <>
      <div
        ref={cardRef}
        data-testid="oml-share-card"
        className="mt-4 mx-auto rounded-3xl overflow-hidden relative"
        style={{
          width: "min(360px, 90vw)",
          aspectRatio: "9 / 16",
          background: "radial-gradient(ellipse at 50% 40%, #1c2748 0%, #0a132a 55%, #050a17 100%)",
          border: `1px solid rgba(224,169,122,0.35)`,
          boxShadow: "0 20px 80px rgba(224,169,122,0.16)",
        }}
      >
        <div className="absolute inset-[8px] rounded-[22px] pointer-events-none"
             style={{ border: "1px solid rgba(224,169,122,0.14)" }} />
        <div className="absolute inset-0 flex flex-col items-center justify-between px-6 pt-10 pb-8 text-center">
          {/* Wordmark */}
          <div className="flex flex-col items-center gap-2">
            <div
              className="uppercase"
              style={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                color: TOK.gold,
                fontSize: "1.35rem",
                fontWeight: 700,
                letterSpacing: "0.14em",
                lineHeight: 1,
              }}
            >
              One Million Lights
            </div>
            <div style={{ width: 42, height: 1.5, background: TOK.gold, opacity: 0.85 }} />
          </div>

          <div className="flex-1 w-full flex flex-col items-center justify-center gap-3">
            <div style={{ width: 110, height: 110 }}>
              <InstallOrb state="playing" size={110} />
            </div>
            <div
              className="uppercase mt-2"
              style={{
                color: TOK.goldSoft,
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.55rem",
                letterSpacing: "0.34em",
              }}
            >
              Founding Light · #{Number(light.number || 0).toLocaleString("en-GB")}
            </div>
            <div
              className="italic leading-none mt-3"
              style={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                color: TOK.gold,
                fontStyle: "italic",
                fontWeight: 500,
                fontSize: intentionDisplay.length > 8 ? "2.6rem" : "3.4rem",
                letterSpacing: "0.01em",
              }}
            >
              {intentionDisplay}
            </div>
            <div
              className="italic leading-tight mt-4"
              style={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                color: TOK.cream,
                fontSize: "1.05rem",
              }}
            >
              I placed my light.
            </div>
            <div
              className="italic leading-tight"
              style={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                color: TOK.creamReadable,
                fontSize: "0.95rem",
              }}
            >
              What will yours be?
            </div>
            {light.display_name ? (
              <div
                className="italic mt-1"
                style={{
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  color: TOK.goldSoft,
                  fontSize: "0.85rem",
                }}
              >
                — {String(light.display_name).split(" ")[0]}
              </div>
            ) : null}
          </div>

          <div
            className="uppercase"
            style={{
              color: TOK.goldSoft,
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.55rem",
              letterSpacing: "0.36em",
            }}
          >
            {siteUrl}
          </div>
        </div>
      </div>

      <div className="mt-8 flex items-center gap-3 flex-wrap justify-center">
        <button
          onClick={share}
          data-testid="oml-share-btn"
          className="rounded-full"
          style={{
            padding: "14px 28px",
            background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
            border: "1px solid #f5e0c5",
            color: "#1a0f05",
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.82rem",
            fontWeight: 600,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            boxShadow: "0 0 30px rgba(224,169,122,0.28)",
          }}
        >
          Share
        </button>
        <button
          onClick={downloadPng}
          data-testid="oml-download-png-btn"
          disabled={downloading}
          className="rounded-full"
          style={{
            padding: "14px 28px",
            background: "transparent",
            border: `1px solid ${TOK.gold}`,
            color: TOK.gold,
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.82rem",
            fontWeight: 600,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            opacity: downloading ? 0.6 : 1,
          }}
        >
          {downloading ? "Preparing…" : "Download image"}
        </button>
        <button
          onClick={onBack}
          data-testid="oml-share-back-btn"
          className="text-[0.72rem] uppercase tracking-[0.24em]"
          style={{ color: TOK.creamReadable, fontFamily: '"Manrope", sans-serif' }}
        >
          ← Back
        </button>
      </div>

      <p
        className="mt-4 text-[0.9rem] italic max-w-sm leading-relaxed"
        style={{ color: TOK.creamReadable, fontFamily: '"Cormorant Garamond", Georgia, serif' }}
      >
        Tap Share to send this on, or Download image for a print-ready
        1080×1920 version. Every light travels further when it is passed on.
      </p>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * Below-the-fold — Explore the vision (all the copy that used to
 * clutter the arrival screen, now hidden until asked for).
 * ────────────────────────────────────────────────────────────── */
function ExploreVisionSection({ count }) {
  const doorways = [
    { id: "reclaim",     n: "01", title: "Reclaim",             blurb: "A place to return to yourself — whatever your circumstances." },
    { id: "atelier",     n: "02", title: "The Atelier",         blurb: "The transformational heart: a place to consciously re-architect the inner world." },
    { id: "soulumo",     n: "03", title: "Soulumo",             blurb: "Personal installations shaped through your words, voice, music, frequency and intention." },
    { id: "absorption",  n: "04", title: "The Absorption Engine", blurb: "A calmer way to help knowledge become familiar rather than forcing it into memory." },
    { id: "foundations", n: "05", title: "Soulumo Foundations", blurb: "A quiet, curated collection for anyone finding their footing — Morning Light, Peace, Self-Belief, Rest." },
    { id: "daily",       n: "06", title: "The Daily Routine",   blurb: "A simple daily rhythm of gratitude, activation, intention, immersion, reset and reflection. The first step is free." },
  ];
  return (
    <div className="mt-10">
      <div className="max-w-md mx-auto mb-10 px-2"><GuideLiteEntry to="/lights/guide" label="One Million Lights" /></div>
      <div className="text-center">
        <div
          className="text-[0.55rem] uppercase tracking-[0.4em]"
          style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
        >
          The world behind the lights
        </div>
        <h3
          className="mt-3 font-light"
          style={{
            fontFamily: '"Cormorant Garamond", serif',
            color: TOK.cream,
            fontSize: "clamp(1.7rem, 5vw, 2.3rem)",
          }}
        >
          Six doorways. <em style={{ color: TOK.gold }}>One open promise.</em>
        </h3>
        <p
          className="mt-4 italic max-w-md mx-auto leading-relaxed"
          style={{ color: TOK.creamReadable, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.02rem" }}
        >
          Every experience is born from the same belief: the tools to change a
          life should not belong only to those who can afford them.
        </p>
      </div>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {doorways.map((d) => {
          // Feb 27 2026 · Michael picked "Option A · Signature Motif"
          // from /preview-lab. Each doorway now renders a hand-crafted
          // SVG scene (no image dependency, no CDN weight, animates
          // gently) instead of the broken screenshot tiles.
          const Motif = MOTIF_BY_ID[d.id];
          return (
            <div
              key={d.id}
              className="rounded-2xl overflow-hidden transition-transform"
              style={{
                background: "rgba(245,236,217,0.03)",
                border: `1px solid ${TOK.goldMist}`,
                boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
              }}
              data-testid={`oml-doorway-${d.id}`}
            >
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  aspectRatio: "16 / 10",
                  overflow: "hidden",
                  borderBottom: `1px solid ${TOK.goldMist}`,
                }}
              >
                {Motif ? <Motif /> : null}
                {/* Top + bottom vignette so the numeral chip sits cleanly. */}
                <div
                  aria-hidden
                  style={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(180deg, rgba(5,10,23,0.45) 0%, transparent 40%, transparent 65%, rgba(5,10,23,0.45) 100%)",
                    pointerEvents: "none",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    top: 10,
                    left: 12,
                    fontSize: "0.52rem",
                    letterSpacing: "0.36em",
                    textTransform: "uppercase",
                    color: TOK.goldSoft,
                    fontFamily: '"Manrope", sans-serif',
                  }}
                >
                  Creation {d.n}
                </div>
              </div>
              <div className="p-4">
                <div
                  className="font-light"
                  style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: "1.35rem", color: TOK.cream }}
                >
                  {d.title}
                </div>
                <p
                  className="mt-2 italic text-[0.92rem] leading-relaxed"
                  style={{ color: TOK.creamReadable, fontFamily: '"Cormorant Garamond", serif' }}
                >
                  {d.blurb}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-10 text-center max-w-md mx-auto">
        {count && <ProgressRing placed={count.display || 0} target={1_000_000} />}
        <p
          className="mt-6 italic leading-relaxed"
          style={{ color: TOK.creamReadable, fontFamily: '"Cormorant Garamond", serif', fontSize: "1.02rem" }}
        >
          Contributions begin at £1 and help build and distribute Soulumo
          Foundations, so we can keep opening this small place of care to
          people who otherwise could not take part. We will share clear
          progress updates as the work grows.
        </p>
      </div>
    </div>
  );
}

/* ── Progress ring — subtle "X of 1,000,000 lights placed" ─────
 * SVG circle with the count centred. Gold arc grows with lights.
 * Respects reduced-motion (no animation, static state). */
function ProgressRing({ placed, target }) {
  const size = 220;
  const stroke = 8;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(1, placed / target));
  const offset = c * (1 - pct);
  return (
    <div className="flex flex-col items-center" data-testid="oml-progress-ring">
      <svg width={size} height={size}>
        {/* Base ring */}
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke="rgba(224,169,122,0.18)" strokeWidth={stroke}
        />
        {/* Gold arc */}
        <circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none"
          stroke="url(#oml-progress-grad)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dashoffset 0.8s ease-out" }}
        />
        <defs>
          <linearGradient id="oml-progress-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f0c790" />
            <stop offset="100%" stopColor="#d99b6b" />
          </linearGradient>
        </defs>
      </svg>
      <div style={{ marginTop: -142 }} className="flex flex-col items-center">
        <div
          className="text-[0.55rem] uppercase tracking-[0.36em]"
          style={{ color: TOK.goldSoft, fontFamily: '"Manrope", sans-serif' }}
        >
          So far
        </div>
        <div
          className="font-light mt-1"
          style={{
            fontFamily: '"Cormorant Garamond", serif',
            fontSize: "clamp(2rem, 6vw, 2.6rem)",
            color: TOK.cream,
            lineHeight: 1,
          }}
        >
          {Number(placed || 0).toLocaleString("en-GB")}
        </div>
        <div
          className="mt-1 text-[0.62rem] uppercase tracking-[0.28em]"
          style={{ color: TOK.creamReadable, fontFamily: '"Manrope", sans-serif' }}
        >
          of {Number(target).toLocaleString("en-GB")} lights
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * Background — a very small starfield. Respects reduced-motion.
 * ────────────────────────────────────────────────────────────── */
function BackgroundStars({ reduceMotion }) {
  return (
    <>
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          // Feb 25 2026 · Toned the star opacities down (0.5→0.3
          // etc) after Michael flagged the field as distracting on
          // the reading section. Still enough sparkle to feel like
          // a live sky; not enough to compete with the words.
          background: `
            radial-gradient(1px 1px at 20% 30%, rgba(245,236,217,0.3) 50%, transparent 50%),
            radial-gradient(1px 1px at 65% 20%, rgba(245,236,217,0.22) 50%, transparent 50%),
            radial-gradient(1px 1px at 80% 55%, rgba(245,236,217,0.2) 50%, transparent 50%),
            radial-gradient(1px 1px at 30% 70%, rgba(245,236,217,0.18) 50%, transparent 50%),
            radial-gradient(1px 1px at 50% 85%, rgba(245,236,217,0.22) 50%, transparent 50%)
          `,
          backgroundSize: "500px 500px",
          animation: reduceMotion ? undefined : "oml-drift 60s linear infinite",
        }}
        aria-hidden
      />
      <style>{`
        @keyframes oml-drift {
          from { background-position: 0 0; }
          to   { background-position: -500px 500px; }
        }
      `}</style>
    </>
  );
}


/* ─────────────────────────────────────────────────────────────────
 * OmlFaqsSection — minimalist FAQ list. Feb 10 2026.
 * Rendered inline below the fold when the visitor taps the FAQs
 * chip on the arrival screen. Elegant open/close cards, no long
 * marketing copy.
 * ────────────────────────────────────────────────────────────── */
const OML_FAQS = [
  {
    q: "What is One Million Lights?",
    a: "A global thank-you to the people quietly holding the world together. Our mission is one million lights for carers and the people they care for — each light opening a beautifully simple place to pause, breathe, rest and receive something for themselves.",
  },
  {
    q: "What actually happens when I place a light?",
    a: "Your gift opens Soulumo Foundations for one carer — a gentle circle of care for their mind, their body, and the person behind the caring role. Your light appears on the globe as our visual thank-you to the people rarely thanked enough.",
  },
  {
    q: "Where does my £1 go?",
    a: "Direct to opening Soulumo Foundations for one person. Each light also carries a small share of the essential running costs of building and hosting the free experience. We show a public running total and honest updates as the movement grows.",
  },
  {
    q: "Who is a carer?",
    a: "Anyone quietly caring for an elderly parent, a partner through illness, a child with additional needs, a relative, a friend or a neighbour going through something hard. Many carers do not call themselves carers. They simply keep showing up.",
  },
  {
    q: "How are carers chosen?",
    a: "Anyone can nominate a carer during the Place a Light flow, in honour of someone, or leave the light unrouted. Unrouted lights flow to participating charity and community partners who work with unseen carers and the people they care for.",
  },
  {
    q: "Can I nominate a carer I know?",
    a: "Yes — you can nominate a carer directly during the Place a Light flow, or in honour of someone. When Phase 2 of the flow ships, you will also be able to send them a private invitation and see their light appear at the centre of a small constellation on the globe.",
  },
  {
    q: "Is this therapy or medical care?",
    a: "No. Soulumo Foundations is a supportive wellbeing experience designed to help someone pause, regulate, rest and reconnect. It complements — but does not replace — professional medical or mental-health support.",
  },
  {
    q: "How do you protect privacy?",
    a: "We only display a general country-level location on the globe. We never publish exact addresses, names or anything that could identify an individual carer or the person they support.",
  },
  {
    q: "How is the 1,000,000 counted?",
    a: "The counter shows lights placed. A light is only counted as reaching a carer once the invitation has been activated by the recipient. We never inflate the number with lights that were only generated or link-shared.",
  },
  {
    q: "What is a Founding Light?",
    a: "Our small thank-you to those who arrive early. Founding Lights receive 40% off their first year of Soulumo, and a place in the Founding Circle. Please place a light first — this is only our way of thanking you back.",
  },
];

function OmlFaqsSection({ onClose }) {
  const [openIdx, setOpenIdx] = useState(-1);
  return (
    <section className="mt-14" data-testid="oml-faqs-section">
      <div className="flex items-baseline justify-between gap-3 mb-6">
        <h2
          className="uppercase"
          style={{
            color: "rgba(224,169,122,0.92)",
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.62rem",
            letterSpacing: "0.36em",
          }}
        >
          ✦  Frequently Asked  ✦
        </h2>
        {onClose && (
          <button
            onClick={onClose}
            data-testid="oml-faqs-close"
            style={{
              color: "rgba(245,236,217,0.5)",
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.6rem",
              letterSpacing: "0.28em",
              textTransform: "uppercase",
            }}
          >
            Close
          </button>
        )}
      </div>
      <div className="space-y-2">
        {OML_FAQS.map((f, i) => {
          const open = openIdx === i;
          return (
            <div
              key={i}
              className="rounded-2xl px-5 py-4"
              style={{
                background: "rgba(245,236,217,0.035)",
                border: "1px solid rgba(224,169,122,0.22)",
              }}
              data-testid={`oml-faq-${i}`}
            >
              <button
                type="button"
                onClick={() => setOpenIdx(open ? -1 : i)}
                data-testid={`oml-faq-toggle-${i}`}
                className="w-full flex items-center justify-between gap-3 text-left"
                aria-expanded={open}
              >
                <span
                  className="italic"
                  style={{
                    color: "#f5ecd9",
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: "1.08rem",
                    fontWeight: 300,
                    lineHeight: 1.3,
                  }}
                >
                  {f.q}
                </span>
                <span
                  style={{
                    color: "rgba(224,169,122,0.85)",
                    fontFamily: '"Manrope", sans-serif',
                    fontSize: "1rem",
                    fontWeight: 300,
                    transform: open ? "rotate(45deg)" : "rotate(0deg)",
                    transition: "transform 220ms ease",
                  }}
                >
                  +
                </span>
              </button>
              {open && (
                <p
                  className="mt-3 italic leading-relaxed"
                  style={{
                    color: "rgba(245,236,217,0.82)",
                    fontFamily: '"Cormorant Garamond", serif',
                    fontSize: "0.98rem",
                  }}
                >
                  {f.a}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * OmlGiftInfoSection — explains what the actual gift IS.
 * Feb 11 2026 · Michael's ask: replace the WhatsApp share sheet with
 * an inline info card so visitors understand a gifted light opens
 * Soulumo Foundations for someone else. Primary CTA: Place a light.
 * ────────────────────────────────────────────────────────────── */
function OmlGiftInfoSection({ onPlaceLight, onJoinFree, flJoined, onClose }) {
  const bullets = [
    {
      title: "A gifted Foundations membership",
      body: "Every £1 light opens the free Soulumo Foundations morning + night audio routine for one person who otherwise could not afford it.",
    },
    {
      title: "Nine core installations",
      body: "The gift includes the full Foundations catalogue — installations covering calm, self-belief, focus, rest and daily identity, layered with music and gentle binaural tones.",
    },
    {
      title: "Delivered as a partner voucher",
      body: "For charity and community partners, we mint printable voucher cards. The recipient redeems the code and lands in Soulumo instantly, no card needed.",
    },
    {
      title: "The recipient stays anonymous",
      body: "You never see who receives the gift. Your light is simply added to the map so the movement can keep opening doors.",
    },
  ];
  return (
    <section className="mt-14" data-testid="oml-gift-section">
      <div className="flex items-baseline justify-between gap-3 mb-6">
        <h2
          className="uppercase"
          style={{
            color: "rgba(224,169,122,0.92)",
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.62rem",
            letterSpacing: "0.36em",
          }}
        >
          ✦  What is the gift?  ✦
        </h2>
        {onClose && (
          <button
            onClick={onClose}
            data-testid="oml-gift-close"
            style={{
              color: "rgba(245,236,217,0.5)",
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.6rem",
              letterSpacing: "0.28em",
              textTransform: "uppercase",
            }}
          >
            Close
          </button>
        )}
      </div>

      <p
        className="italic leading-relaxed mb-6"
        style={{
          color: "#f5ecd9",
          fontFamily: '"Cormorant Garamond", serif',
          fontSize: "1.15rem",
        }}
      >
        When you place a light, the gift is not a keepsake or a thank-you
        note. The gift is <span style={{ color: "#e0a97a" }}>Soulumo Foundations</span>
        {" "}— opened for a real person who could not otherwise begin.
      </p>

      <div className="space-y-3">
        {bullets.map((b, i) => (
          <div
            key={i}
            className="rounded-2xl px-5 py-4"
            style={{
              background: "rgba(245,236,217,0.035)",
              border: "1px solid rgba(224,169,122,0.22)",
            }}
            data-testid={`oml-gift-bullet-${i}`}
          >
            <h3
              className="uppercase"
              style={{
                color: "rgba(224,169,122,0.92)",
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.62rem",
                letterSpacing: "0.28em",
                fontWeight: 600,
              }}
            >
              {b.title}
            </h3>
            <p
              className="mt-2 leading-relaxed"
              style={{
                color: "rgba(245,236,217,0.86)",
                fontFamily: '"Cormorant Garamond", serif',
                fontSize: "1.02rem",
              }}
            >
              {b.body}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-8 flex flex-col gap-3">
        <button
          onClick={onPlaceLight}
          data-testid="oml-gift-place-light"
          className="rounded-full transition-all w-full"
          style={{
            background: "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)",
            border: "1px solid #f5e0c5",
            color: "#1a0f05",
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.86rem",
            fontWeight: 700,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            padding: "18px 26px",
            boxShadow: "0 0 44px rgba(224,169,122,0.4), 0 8px 24px rgba(0,0,0,0.35)",
          }}
        >
          Place a Light · From £1
        </button>
        <button
          onClick={onJoinFree}
          data-testid="oml-gift-join-free"
          className="rounded-full transition-all w-full"
          style={{
            background: flJoined ? "rgba(224,169,122,0.14)" : "rgba(19,31,56,0.55)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            border: `1.5px solid ${flJoined ? "#e0a97a" : "#f5e0c5"}`,
            color: flJoined ? "#e0a97a" : "#f5ecd9",
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.78rem",
            fontWeight: 600,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            padding: "16px 26px",
          }}
        >
          {flJoined ? "✓  Founding Light Reserved" : "Reserve as a Founding Light — Free"}
        </button>
      </div>
    </section>
  );
}


/* ─────────────────────────────────────────────────────────────────
 * GiftRevealBlock — the "What is the gift?" tap reveal
 *
 * Feb 13 2026 · Michael. Simplified per feedback: the Gift chip
 * now shows ONLY the Soulumo Foundations page — the three signature
 * care experiences and the unifying essence. Everything else lives
 * in the Founding or Apps reveals. Ends with a single Place a Light
 * CTA so a visitor who is moved by the gift can act immediately.
 * ────────────────────────────────────────────────────────────── */
function GiftRevealBlock({ onPlaceLight, onClose }) {
  return (
    <div className="mt-6" data-testid="oml-gift-reveal">
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <span
          className="uppercase"
          style={{
            color: "rgba(224,169,122,0.92)",
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.6rem",
            letterSpacing: "0.34em",
          }}
        >
          ✦  Soulumo Foundations  ✦
        </span>
        {onClose && (
          <button
            onClick={onClose}
            data-testid="oml-gift-close"
            style={{
              color: "rgba(245,236,217,0.5)",
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.58rem",
              letterSpacing: "0.28em",
              textTransform: "uppercase",
            }}
          >
            Close
          </button>
        )}
      </div>
      <CircleOfCarePreview />
      <UnifyingEssenceSection />
      <ClosingMillionCarersSection onGiveLight={onPlaceLight} />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────
 * FoundingRevealBlock — the "Founding Light" tap reveal
 *
 * Feb 13 2026 · Michael asked for a fourth chip explaining anything
 * yet unsaid. This groups: what a Founding Light is (thank-you
 * benefit — 40% off ecosystem at launch), the Live Impact band, the
 * partnership doorway for orgs supporting carers, and the full
 * transparency accordion. Deliberately no headline pricing — the
 * £19.99 Soulumo membership number never surfaces on OML.
 * ────────────────────────────────────────────────────────────── */
function FoundingRevealBlock({ onJoinFree, flJoined, onClose, count }) {
  return (
    <div className="mt-6" data-testid="oml-founding-reveal">
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <span
          className="uppercase"
          style={{
            color: "rgba(224,169,122,0.92)",
            fontFamily: '"Manrope", sans-serif',
            fontSize: "0.6rem",
            letterSpacing: "0.34em",
          }}
        >
          ✦  Founding Light  ✦
        </span>
        {onClose && (
          <button
            onClick={onClose}
            data-testid="oml-founding-close"
            style={{
              color: "rgba(245,236,217,0.5)",
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.58rem",
              letterSpacing: "0.28em",
              textTransform: "uppercase",
            }}
          >
            Close
          </button>
        )}
      </div>
      <FoundingLightThankYouSection onJoinFree={onJoinFree} flJoined={flJoined} />
      <LiveImpactBand count={count} />
      <PartnershipSection />
      <TransparencySection />
    </div>
  );
}

